import React, { FC, useCallback } from 'react';
import { IntegrationField } from 'api/integrations';
import { InputNumber } from 'components/atoms/input';
import { useNumericInput } from 'hooks/use-input';

type OnChangeParams = {
  integrationFieldId: string;
  valueStr?: number;
};

type IntegrationNumberField = {
  field: IntegrationField;
  onChange?: (data: OnChangeParams) => void;
  value?: number;
  disabled?: boolean;
  placeholder?: string;
};

const parseInput = (val: string | undefined): number | undefined => {
  if (val == null) {
    return undefined;
  }

  const convertedNumber = parseInt(val, 10);

  if (!Number.isNaN(convertedNumber)) {
    return convertedNumber;
  }

  return undefined;
};

export const IntegrationNumberField: FC<IntegrationNumberField> = ({
  field,
  onChange,
  value,
  placeholder = '',
  disabled,
}) => {
  const [newValue, onInputChange, setValue] = useNumericInput(value ?? 0);
  const { integrationFieldId } = field;

  const handlePressEnter = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const convertedValue = parseInput((e.target as HTMLInputElement).value);

      if (onChange != null) {
        onChange({
          integrationFieldId,
          valueStr: convertedValue,
        });
      } else {
        e.currentTarget?.blur();
      }
    },
    [onChange, integrationFieldId],
  );

  const handleOnBlur = useCallback(
    (_: React.FocusEvent<HTMLInputElement>) => {
      if (onChange != null) {
        onChange({
          integrationFieldId,
          valueStr: newValue,
        });
      }
    },
    [onChange, integrationFieldId, newValue],
  );

  return (
    <InputNumber
      min={field.metaData?.range?.min}
      max={field.metaData?.range?.max}
      placeholder={placeholder}
      value={newValue}
      onInput={onInputChange}
      onChange={(num) => setValue(num)}
      onPressEnter={handlePressEnter}
      onBlur={handleOnBlur}
      disabled={disabled}
    />
  );
};
